import React from 'react';
import { Link } from 'react-router-dom';
import {
  Gauge, LineChart, Zap, TrendingUp, CheckCircle2, Rocket, UsersRound, ArrowLeft, ArrowRight, Compass
} from 'lucide-react';

const features = [
  {
    icon: LineChart,
    title: 'Business Impact Correlation',
    description: 'Map quality metrics directly to revenue. Answer "Which bugs impact revenue?" in language executives understand.',
    bullets: ['Quality metrics → revenue impact', 'Bug severity → customer churn', 'Executive dashboards in business language'],
    accent: 'from-purple-500 to-pink-500',
    accentColor: '#a855f7',
    value: 'Quality → Revenue',
  },
  {
    icon: Gauge,
    title: 'One Dashboard, Every Tool',
    description: 'Intelligent aggregators unify data from Jenkins, Jira, SonarQube, TestRail, Rally — technical depth for engineers, clarity for executives.',
    bullets: ['Customisable to your exact tool stack', 'Replaces 10+ tool logins', 'Updates every 15 minutes — zero manual entry'],
    accent: 'from-blue-500 to-cyan-500',
    accentColor: '#00d4ff',
    value: 'Saves ~2 hrs/day',
  },
  {
    icon: Zap,
    title: 'Flaky Test Intelligence',
    description: 'AI pattern recognition spots flaky tests automatically and suggests root causes — restoring trust in your CI pipeline.',
    bullets: ['AI-powered flaky test detection', 'Root-cause suggestions & auto-quarantine', 'Restore confidence in your pipeline'],
    accent: 'from-emerald-500 to-teal-500',
    accentColor: '#10b981',
    value: 'Fewer false failures',
  },
  {
    icon: TrendingUp,
    title: 'Comprehensive KPI Tracking',
    description: '22 metrics across Quality, Speed, Agile Process, and Reliability — with historical trends, targets, and DORA support.',
    bullets: ['Build success, MTTR & deployment frequency', 'Coverage, debt & defect density', '30-day trends and targets'],
    accent: 'from-orange-500 to-amber-500',
    accentColor: '#f59e0b',
    value: '22 metrics, one view',
  },
  {
    icon: UsersRound,
    title: 'AI Team Insights',
    description: 'Team-level AI analytics that surface collaboration patterns, bottlenecks, and delivery risks before they hit your release dates.',
    bullets: ['Cross-team risk analysis', 'Per-developer productivity insights', 'Automated retrospective takeaways'],
    accent: 'from-fuchsia-500 to-pink-500',
    accentColor: '#ec4899',
    value: 'AI-powered',
    isNew: true,
  },
];

const stats = [
  { value: '6-Figure', label: 'Annual ROI potential' },
  { value: '<12 Mo', label: 'Payback period' },
  { value: '15 Years', label: 'QA experience behind it' },
  { value: '10+', label: 'Tools unified' },
];

const integrations = [
  'Jenkins', 'Jira', 'GitHub', 'GitLab', 'Azure DevOps',
  'CircleCI', 'SonarQube', 'TestRail', 'Rally', 'Selenium Grid',
  'Grafana', 'Datadog', 'New Relic', 'Custom Tools',
];

const NavigatorPage = () => {
  return (
    <div className="min-h-screen" style={{ background: '#080d1a' }}>
      {/* Mini nav */}
      <div className="fixed top-0 left-0 right-0 z-50 border-b"
        style={{ background: 'rgba(8,13,26,0.95)', backdropFilter: 'blur(20px)', borderColor: 'rgba(0,212,255,0.12)' }}>
        <div className="max-w-7xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Link to="/" className="flex items-center space-x-1.5 text-sm text-gray-400 hover:text-white transition-colors">
              <ArrowLeft className="w-4 h-4" />
              <span>IronGate</span>
            </Link>
            <span className="text-gray-700">/</span>
            <div className="flex items-center space-x-1.5">
              <Compass className="w-4 h-4 text-cyan-400" />
              <span className="text-sm font-semibold text-white">QE Navigator</span>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <a href="https://demo.irongate.ie" target="_blank" rel="noopener noreferrer"
              className="text-sm text-gray-400 hover:text-white transition-colors hidden sm:block">
              Try Demo
            </a>
            <a href="#navigator-contact"
              onClick={e => { e.preventDefault(); document.getElementById('navigator-contact')?.scrollIntoView({ behavior: 'smooth' }); }}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-white"
              style={{ background: 'linear-gradient(135deg, #00b8e6 0%, #0066ff 100%)' }}>
              Request Demo
            </a>
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-center justify-center pt-20 overflow-hidden">
        <div className="absolute inset-0 circuit-pattern opacity-25" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full blur-3xl pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(0,212,255,0.07) 0%, transparent 70%)' }} />

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="inline-flex items-center space-x-2 rounded-full px-4 py-1.5 mb-8"
            style={{ background: 'rgba(0,212,255,0.08)', border: '1px solid rgba(0,212,255,0.3)' }}>
            <Compass className="w-3.5 h-3.5 text-cyan-400" />
            <span className="text-xs font-medium text-cyan-400 tracking-wide uppercase">A product by IronGate Software</span>
          </div>

          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            <span className="gradient-text">QE Navigator</span>
            <br />
            <span className="text-white text-4xl md:text-5xl font-semibold">Turn Quality Into Revenue</span>
          </h1>

          <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto leading-relaxed">
            Intelligent aggregators that unify data from Jenkins, Jira, SonarQube, and more into one dashboard.
            Technical metrics for engineers. Business impact for executives. Same data, both languages.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
            <a href="https://demo.irongate.ie" target="_blank" rel="noopener noreferrer"
              className="group inline-flex items-center space-x-2 px-8 py-3.5 rounded-lg font-semibold text-white transition-all"
              style={{ background: 'linear-gradient(135deg, #00b8e6 0%, #0066ff 100%)' }}>
              <span>Try the Live Demo</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
            <a href="/presentation.html" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 px-8 py-3.5 rounded-lg font-semibold transition-all"
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(0,212,255,0.3)',
                color: '#00d4ff',
              }}>
              <span>View Full Presentation</span>
            </a>
          </div>
          <p className="text-sm text-gray-600">
            Or{' '}
            <a href="#navigator-contact"
              onClick={e => { e.preventDefault(); document.getElementById('navigator-contact')?.scrollIntoView({ behavior: 'smooth' }); }}
              className="text-cyan-500 hover:text-cyan-400 underline">
              request a personalised demo
            </a>
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 border-y" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((s, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl font-bold gradient-text mb-1">{s.value}</div>
                <div className="text-xs text-gray-500">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 rounded-full px-4 py-1.5 mb-6"
              style={{ background: 'rgba(0,212,255,0.08)', border: '1px solid rgba(0,212,255,0.25)' }}>
              <Rocket className="w-3.5 h-3.5 text-cyan-400" />
              <span className="text-xs font-medium text-cyan-400 tracking-wide uppercase">Platform Features</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-5">
              Everything we <span className="gradient-text">wished we had</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              15 years of QA leadership pain, distilled into software. Every feature solves a problem we lived.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i}
                  className="group relative rounded-2xl p-7 transition-all duration-300 hover:-translate-y-1 overflow-hidden"
                  style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = `${f.accentColor}35`}
                  onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'}
                >
                  <div className={`absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r ${f.accent} opacity-0 group-hover:opacity-100 transition-opacity`} />

                  <div className="flex items-start justify-between mb-5">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${f.accent} p-2.5`}>
                      <Icon className="w-full h-full text-white" />
                    </div>
                    <div className="flex items-center gap-2">
                      {f.isNew && (
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full"
                          style={{ background: 'rgba(168,85,247,0.15)', border: '1px solid rgba(168,85,247,0.3)', color: '#c084fc' }}>
                          ✨ NEW
                        </span>
                      )}
                      <span className="text-xs font-medium px-2.5 py-0.5 rounded-full"
                        style={{ background: 'rgba(0,212,255,0.1)', border: '1px solid rgba(0,212,255,0.2)', color: '#00d4ff' }}>
                        {f.value}
                      </span>
                    </div>
                  </div>

                  <h3 className="text-lg font-bold text-white mb-2">{f.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed mb-5">{f.description}</p>

                  <ul className="space-y-2">
                    {f.bullets.map((b, j) => (
                      <li key={j} className="flex items-start space-x-2 text-sm text-gray-500">
                        <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: f.accentColor }} />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Integrations */}
      <section className="py-16 border-y" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-2xl font-bold text-white mb-3">Works With Your Tool Stack</h3>
          <p className="text-gray-400 mb-8 max-w-xl mx-auto text-sm">
            QE Navigator integrates with any tools you currently use. Just tell us your stack — we'll customise the integrations.
          </p>
          <div className="flex flex-wrap justify-center gap-2.5">
            {integrations.map((tool, i) => (
              <span key={i} className="px-3.5 py-1.5 rounded-lg text-sm text-gray-400"
                style={{ background: 'rgba(0,212,255,0.08)', border: '1px solid rgba(0,212,255,0.2)' }}>
                {tool}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="navigator-contact" className="py-24">
        <div className="max-w-xl mx-auto px-4 sm:px-6 text-center">
          <div className="rounded-2xl p-10"
            style={{
              background: 'linear-gradient(135deg, rgba(0,212,255,0.07) 0%, rgba(0,102,255,0.07) 100%)',
              border: '1px solid rgba(0,212,255,0.2)',
            }}>
            <Compass className="w-10 h-10 text-cyan-400 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-white mb-3">Ready to see it in action?</h2>
            <p className="text-gray-400 mb-8 text-sm leading-relaxed">
              Book a personalised demo and see how QE Navigator maps your quality data to business outcomes — in 30 minutes.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href="https://demo.irongate.ie" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center justify-center space-x-2 px-6 py-3 rounded-lg font-semibold text-white text-sm"
                style={{ background: 'linear-gradient(135deg, #00b8e6 0%, #0066ff 100%)' }}>
                <span>Try Live Demo</span>
                <ArrowRight className="w-4 h-4" />
              </a>
              <a href="mailto:contact@irongate.ie?subject=QE Navigator Demo Request"
                className="inline-flex items-center justify-center px-6 py-3 rounded-lg font-semibold text-sm transition-all"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', color: '#e2e8f0' }}>
                Request a Demo
              </a>
            </div>
            <p className="text-xs text-gray-600 mt-5">
              Or email us at{' '}
              <a href="mailto:contact@irongate.ie" className="text-cyan-500 hover:text-cyan-400 underline">
                contact@irongate.ie
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <div className="border-t py-8 text-center text-xs text-gray-600"
        style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
        <Link to="/" className="text-gray-500 hover:text-cyan-400 transition-colors">← Back to IronGate.ie</Link>
        <span className="mx-4 text-gray-700">·</span>
        <span>© {new Date().getFullYear()} IronGate Software LTD</span>
      </div>
    </div>
  );
};

export default NavigatorPage;
